import { useEffect, useState } from "react";
import { Search, Plus, Check, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useEventCart } from "@/context/EventCartContext";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import SponsorBanner from "@/components/SponsorBanner";

export default function Shows() {
  const [shows, setShows] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const { items, addShow, removeShow } = useEventCart();

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines(r.data));
  }, []);
  useEffect(() => {
    setLoading(true);
    api
      .get("/shows", { params: { q, category: cat } })
      .then((r) => setShows(r.data))
      .finally(() => setLoading(false));
  }, [q, cat]);

  const isSelected = (id) => items.shows.some((s) => s.id === id);

  return (
    <div className="pt-28 pb-16">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="av-eyebrow">Catalogo Spettacoli</span>
        <h1 className="font-serif-display text-4xl sm:text-6xl mt-4 leading-tight">
          Format già prodotti, <span className="italic text-[#E5A93C]">pronti</span> ad
          andare in scena.
        </h1>

        <div className="mt-10 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A39E93]"
            />
            <input
              className="av-input pl-9"
              placeholder="Cerca uno spettacolo"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="shows-search-input"
            />
          </div>
          <select
            className="av-input max-w-xs"
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            data-testid="shows-category-select"
          >
            <option value="">Tutte le categorie</option>
            {disciplines.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-[#A39E93] mt-16">Caricamento…</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-10">
            {shows.map((s) => {
              const selected = isSelected(s.id);
              return (
                <article
                  key={s.id}
                  className="av-card overflow-hidden group"
                  data-testid={`show-card-${s.id}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    {s.image_url ? (
                      <img
                        src={s.image_url}
                        alt={s.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#1d1a17]" />
                    )}
                    <div className="absolute top-3 left-3 av-eyebrow text-[10px]">
                      {s.category}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif-display text-2xl">{s.title}</h3>
                    {s.duration_min && (
                      <div className="flex items-center gap-1 text-xs text-[#A39E93] mt-1">
                        <Clock size={12} /> {s.duration_min} min
                      </div>
                    )}
                    <p className="text-sm text-[#F7F4EE]/70 mt-3 line-clamp-3">
                      {s.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {(s.tags || []).map((t) => (
                        <span
                          key={t}
                          className="text-[11px] px-2 py-1 rounded-full border av-hairline text-[#F7F4EE]/80"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-5">
                      <div className="text-[#E5A93C] font-mono text-sm">
                        {s.price_from ? `da € ${s.price_from.toLocaleString()}` : "—"}
                      </div>
                      <button
                        className={`av-pill text-sm ${selected ? "av-pill-ghost" : "av-pill-gold"}`}
                        onClick={() => {
                          if (selected) {
                            removeShow(s.id);
                            toast("Rimosso dalla bozza evento");
                          } else {
                            addShow(s);
                            toast.success(`${s.title} aggiunto alla bozza`);
                          }
                        }}
                        data-testid={`show-add-btn-${s.id}`}
                      >
                        {selected ? (
                          <>
                            <Check size={14} /> Selezionato
                          </>
                        ) : (
                          <>
                            <Plus size={14} /> Aggiungi
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
            {!shows.length && (
              <p className="text-[#A39E93] col-span-full">Nessuno spettacolo trovato.</p>
            )}
          </div>
        )}
      </section>
      <div className="mt-16">
        <SponsorBanner slot="shows" />
      </div>
      <Footer />
    </div>
  );
}
